import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

/**
 * DurabilityPage — "we're not going anywhere," proven by infrastructure. (CO 032-004)
 *
 * For a control-first operator betting their WHOLE business on an OS, the
 * unspoken first question is "will you still be here?" This page answers it with
 * the infrastructure that makes the bet safe — and, like everything else on this
 * site, with claims that survive the closest read.
 *
 * ⚠️ Every infra claim below was audited TRUE against the running deployment
 * before publish (see CO 032-004 playbook). Serverless = Cloud Run; load-balanced
 * = Cloud Run autoscaling + Cloud Load Balancer; redundant = MongoDB Atlas M10+
 * 3-node replica set; per-tenant DB = PHYSICAL db-per-tenant (getTenantDb →
 * `${tenantId}-comai`). No claim exceeds the audit. Do not add infra copy here
 * without re-running the audit.
 */
const HERO = PAGE_HEROES['durability']!;

export function DurabilityPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero — the real first question */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Running your whole operation on an OS means one question comes
              before all the others: will it still be here, and will it stay up
              while it is? The answer isn't a promise — it's the infrastructure.
              No single box to fall over, no single database to lose, and your
              data in a space that's physically yours.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* The four infrastructure facts */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="Built to outlast"
            title="The infrastructure underneath the promise."
            lead="Four facts about how ComOS runs. Each one is a way the system survives a failure that would take a lesser setup down."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "Serverless — no single box to fall over",
                v: "Every service runs on Google Cloud Run, a fully managed serverless platform. There's no server we keep alive by hand and no single machine whose failure takes you offline. Instances come and go; the service stays up.",
              },
              {
                k: "Load-balanced — it scales with the work",
                v: "Traffic is load-balanced across instances that auto-scale with demand, and the critical services are kept warm so there's no cold start when you need them. A spike grows the fleet; it doesn't knock it over.",
              },
              {
                k: "Redundant databases — survive any one failure",
                v: "Production data lives in a MongoDB Atlas replica set — three nodes with automatic failover. Any single node can fail without losing a transaction; the database keeps serving from the others while it heals.",
              },
              {
                k: "Your own private database",
                v: "Each tenant's data lives in its own separate database — physical isolation, not a shared table with a filter column. The database engine itself keeps tenants apart, so another tenant's problem is never your problem.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Your data is isolated — the trust framing of per-tenant DB */}
      <Section>
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Your data is yours</p>
            <h2 className="text-display-md text-balance">
              A separate database per tenant isn't an optimization. It's a trust
              boundary.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              Most platforms pool every customer's data into shared tables and
              rely on a filter to keep them apart — one missed condition and the
              wall is gone. ComOS puts each tenant in its own database. The
              isolation is enforced by the database engine, not by remembering
              to add a clause. A breach of someone else's tenant is not a breach
              of yours.
            </p>
            <div className="mt-8">
              <Link
                to="/security"
                className={buttonStyles({ variant: "ghost" })}
              >
                See the full security posture →
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>

      {/* The company isn't the load-bearing part — tie to Trust */}
      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">
              Why "we're not going anywhere" is more than a slogan
            </p>
            <h2 className="text-display-md text-balance">
              Even durability is something you can verify, not something we ask
              you to believe.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              Infrastructure keeps the system up. The discipline keeps it
              correct. Every change to the OS passes a QC gate before it ships,
              and when something is wrong the record corrects itself in the
              open. The system keeps healing itself — which is the real reason
              it lasts. The company being here matters less when the substrate
              is built to hold.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/trust"
                className={buttonStyles({ variant: "primary" })}
              >
                How we prove it →
              </Link>
              <Link
                to="/security"
                className={buttonStyles({ variant: "outline" })}
              >
                Security posture
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
